import { isNil } from 'lodash-es';
import { Resource } from '../Resource';
import TimeSteps from '../enums/TimeSteps';
import * as methods from '../methods';
import * as transformers from '../transformers';
import charts from './charts';
import measurements from './measurements';
import { SoilTexture } from './plots';
import waterBalance from './waterBalance';
const rootPath = 'measurements/reports';
/**
 * Because we may encounter incorrect reports
 * We transform undefined or null values to zeros
 */
function getNumberValueFromMeasurementData(data) {
    let valueAsNumber = data.IRR ?? data.DM ?? data.RRC ?? data.ASES ?? data.DI?.dose;
    if (isNil(valueAsNumber)) {
        valueAsNumber = 0;
    }
    if (typeof valueAsNumber === 'string') {
        valueAsNumber = parseFloat(valueAsNumber);
    }
    return valueAsNumber;
}
/**
 * Transforming data field into a value field to remove one level of depth
 */
const extractTypeFromData = (data) => {
    const parsedType = 'DI' in data
        ? 'dripIrrigation'
        : 'IRR' in data
            ? 'irrigation'
            : 'DM' in data
                ? 'dryMatter'
                : 'RRC' in data
                    ? 'rainfalls'
                    : 'ASES' in data
                        ? 'ASES'
                        : null;
    if (!parsedType) {
        // We grab the first type (key) we encounter in the data prop,
        // since we will never encounter a report with two different types
        const type = Object.keys(data)[0];
        throw new Error(`An error occurred while converting measurementReportType unknown type: ${type}`);
    }
    return parsedType;
};
/**
 * Transforming value field into the back end data field
 */
function convertTypeValueToData({ type, value }) {
    if (typeof value === 'object' && !isNil(value)) {
        return {
            DI: value
        };
    }
    else {
        switch (type) {
            case 'irrigation':
                return { IRR: value };
            case 'dryMatter':
                return { DM: value };
            case 'rainfalls':
                return { RRC: value };
            case 'ASES':
                return { ASES: typeof value === 'number' ? `${value}` : value };
            default:
                throw new Error(`An error occurred while converting measurementReportType unknown type: ${type}`);
        }
    }
}
const fromRaw = {
    getCumulativeSum: (cumul) => cumul,
    get: ({ id, plot, timestamp, user, data }) => {
        const type = extractTypeFromData(data);
        switch (type) {
            case 'ASES':
                return {
                    id,
                    plotId: plot,
                    timestamp,
                    userId: user,
                    type: 'ASES',
                    value: data.ASES
                };
            case 'dripIrrigation':
                return {
                    id,
                    plotId: plot,
                    timestamp,
                    userId: user,
                    type: 'dripIrrigation',
                    value: data.DI
                };
            case 'irrigation':
                return {
                    id,
                    plotId: plot,
                    timestamp,
                    userId: user,
                    type: 'irrigation',
                    value: getNumberValueFromMeasurementData(data)
                };
            case 'rainfalls':
                return {
                    id,
                    plotId: plot,
                    timestamp,
                    userId: user,
                    type: 'rainfalls',
                    value: getNumberValueFromMeasurementData(data)
                };
            case 'dryMatter':
                return {
                    id,
                    plotId: plot,
                    timestamp,
                    userId: user,
                    type: 'dryMatter',
                    value: getNumberValueFromMeasurementData(data)
                };
        }
    },
    update: ({ id, plot, timestamp, user, data }) => {
        const type = extractTypeFromData(data);
        switch (type) {
            case 'ASES':
                return {
                    id,
                    plotId: plot,
                    timestamp,
                    userId: user,
                    type: 'ASES',
                    value: data.ASES
                };
            case 'dripIrrigation':
                return {
                    id,
                    plotId: plot,
                    timestamp,
                    userId: user,
                    type: 'dripIrrigation',
                    value: data.DI
                };
            case 'irrigation':
                return {
                    id,
                    plotId: plot,
                    timestamp,
                    userId: user,
                    type: 'irrigation',
                    value: getNumberValueFromMeasurementData(data)
                };
            case 'rainfalls':
                return {
                    id,
                    plotId: plot,
                    timestamp,
                    userId: user,
                    type: 'rainfalls',
                    value: getNumberValueFromMeasurementData(data)
                };
            case 'dryMatter':
                return {
                    id,
                    plotId: plot,
                    timestamp,
                    userId: user,
                    type: 'dryMatter',
                    value: getNumberValueFromMeasurementData(data)
                };
        }
    }
};
const toRaw = ({ plotId, timestamp, type, value }) => {
    return {
        timestamp,
        data: convertTypeValueToData({ type, value }),
        plot: plotId
    };
};
const measurementsReports = Resource((requesterBuilder, config) => {
    const getMany = (query) => requesterBuilder({
        path: rootPath,
        requestCtx: {
            transform: transformers.paginatedResponse(fromRaw.get),
            params: {
                query: {
                    limit: query?.limit,
                    offset: query?.offset,
                    since: query?.since,
                    before: query?.before,
                    span: query?.span,
                    metrics: query?.metrics,
                    plot: query?.plotId
                }
            }
        }
    });
    const keysToInvalidate = [
        getMany().invalidationKey,
        waterBalance(requesterBuilder, config).result().invalidationKey,
        // @ts-expect-error We don't care about specific plot id query params here
        measurements(requesterBuilder, config).plots.histories.get1_1({
            metrics: ['HPOT'],
            span: TimeSteps.perDays
        }).invalidationKey,
        charts(requesterBuilder, config).history({
            base: 0,
            before: 0,
            canUseVPDFromDevice: true,
            deviceId: 0,
            highlightThresholds: false,
            isAggregateBySerial: true,
            metrics: [],
            plotId: 0,
            since: 0,
            soilTexture: SoilTexture.clay,
            stationId: 0,
            timeStep: TimeSteps.perDays
        }).invalidationKey
    ];
    return {
        getMany,
        getPage: methods.getPage({ getMany }),
        get: methods.get({ requesterBuilder, rootPath, fromRaw: fromRaw.get }),
        getCumulativeSum: (query) => requesterBuilder({
            path: `${rootPath}/cumul`,
            requestCtx: {
                transform: fromRaw.getCumulativeSum,
                params: {
                    query: {
                        since: query?.since,
                        before: query?.before,
                        metrics: query?.metrics,
                        plot: query?.plotId
                    }
                }
            }
        }),
        getAllPages: methods.getAllPages({ getMany }),
        // TODO: the toRaw is incorrect and should require the plotId when creating a new user report
        create: () => methods.create({
            rootPath,
            fromRaw: (x) => fromRaw.update(x),
            requesterBuilder,
            toRaw,
            buildKeysToInvalidate: () => keysToInvalidate
        })(),
        update: (reportId) => methods.update({
            rootPath,
            fromRaw: (x) => fromRaw.update(x),
            requesterBuilder,
            toRaw,
            buildKeysToInvalidate: () => keysToInvalidate
        })(reportId),
        delete: methods.deletion({
            rootPath,
            requesterBuilder,
            buildKeysToInvalidate: () => keysToInvalidate
        })
    };
});
export default measurementsReports;
